import { useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { Loader2, Check, X, FileText } from "lucide-react"
import type { PendingCarePlan } from "../types/apcm-ops.types"
import * as api from "../api/apcm-ops.api"
import { useApcmOpsStore } from "../stores/apcmOpsStore"
import { parseApiTimestamp } from "@/lib/datetime"

interface PendingApprovalsProps {
  carePlans: PendingCarePlan[]
  orgId: string
  onRefresh: () => void
}

export function PendingApprovals({ carePlans, orgId, onRefresh }: PendingApprovalsProps) {
  const { executeAction, actionInProgress } = useApcmOpsStore()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allSelected = carePlans.length > 0 && carePlans.every((cp) => selectedIds.has(cp.care_plan_id))

  const handleApprove = async (ids: string[]) => {
    await executeAction("Approve Care Plans", () =>
      api.approveCarePlans(orgId, ids)
    )
    setSelectedIds(new Set())
    onRefresh()
  }

  const handleReject = async (ids: string[]) => {
    await executeAction("Reject Care Plans", () =>
      api.rejectCarePlans(orgId, ids)
    )
    setSelectedIds(new Set())
    onRefresh()
  }

  const isLoading = actionInProgress === "Approve Care Plans" || actionInProgress === "Reject Care Plans"

  if (carePlans.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <div className="rounded-full bg-success/10 p-3 mb-3">
          <Check className="h-6 w-6 text-success" />
        </div>
        <span className="text-sm font-medium">All caught up</span>
        <span className="text-xs text-muted-foreground/70 mt-0.5">No care plans pending approval</span>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 md:gap-3 rounded-lg border border-primary/20 bg-primary/5 p-2.5">
          <span className="text-xs font-medium text-primary">
            {selectedIds.size} selected
          </span>
          <Button
            size="sm"
            disabled={isLoading}
            className="h-7 text-xs bg-primary hover:bg-primary-hover text-white"
            onClick={() => handleApprove(Array.from(selectedIds))}
          >
            {isLoading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
            Approve Selected
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="h-7 text-xs"
            disabled={isLoading}
            onClick={() => handleReject(Array.from(selectedIds))}
          >
            <X className="mr-1 h-3 w-3" />
            Reject Selected
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      <div className="rounded-xl border shadow-sm overflow-x-auto">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedIds(new Set(carePlans.map((cp) => cp.care_plan_id)))
                    } else {
                      setSelectedIds(new Set())
                    }
                  }}
                />
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider">Patient</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider">Provider</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider">Conditions</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider">Created</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {carePlans.map((cp) => (
              <TableRow key={cp.care_plan_id} className="transition-colors">
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(cp.care_plan_id)}
                    onCheckedChange={() => toggleSelect(cp.care_plan_id)}
                  />
                </TableCell>
                <TableCell className="font-medium text-sm whitespace-nowrap">{cp.patient_name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {cp.provider_name || <span className="text-muted-foreground/50">-</span>}
                </TableCell>
                <TableCell>
                  <span className="text-sm tabular-nums">{cp.condition_count}</span>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {parseApiTimestamp(cp.created_at)?.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {cp.pdf_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => api.downloadCarePlanPdf(cp.patient_id, cp.care_plan_id, orgId)}
                      >
                        <FileText className="mr-1 h-3 w-3" />
                        PDF
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-primary hover:bg-primary-hover text-white"
                      disabled={isLoading}
                      onClick={() => handleApprove([cp.care_plan_id])}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      disabled={isLoading}
                      onClick={() => handleReject([cp.care_plan_id])}
                    >
                      Reject
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
